/**
 * Security utilities for preventing XSS and injection attacks
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 * Converts: < > & " ' to their HTML entity equivalents
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Escapes regex special characters for safe pattern matching
 */
export function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Safely highlights search terms in text without XSS vulnerability
 * @param text - The text to search within (already sanitized HTML)
 * @param query - The user's search query (untrusted input)
 * @param highlightClass - Optional CSS classes for the mark tag
 * @returns HTML string with safe <mark> tags around matches
 */
export function safeHighlightSearch(
  text: string, 
  query: string,
  highlightClass: string = 'bg-yellow-200 dark:bg-yellow-900'
): string {
  if (!query || query.trim() === '') return text;
  
  // Escape regex special characters in the query
  const escapedQuery = escapeRegex(query);
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  
  // Replace matched text with safely escaped content in <mark> tags
  return text.replace(regex, (match) => {
    const escapedMatch = escapeHtml(match);
    return `<mark class="${highlightClass}">${escapedMatch}</mark>`;
  });
}
